import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const MarketingFooter: React.FC = () => {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !consent) return;

    setSubmitting(true);
    try {
      const { leadsApi } = await import('../../services/api/leads');
      await leadsApi.submitNewsletter({ email, email_consent: consent });
      setSubmitted(true);
      setEmail('');
      setConsent(false);
    } catch {
      // Error handled by global handler
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-secondary-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="text-xl font-display font-bold text-white">Sprout</span>
            </div>
            <p className="text-sm text-gray-400 dark:text-gray-500 leading-relaxed">
              Nurturing early childhood development through thoughtful observation and CAPS-aligned assessment.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Built for South African ECD centres.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-3">
              <li><Link to="/features" className="text-sm hover:text-white transition-colors">Features</Link></li>
              <li><Link to="/pricing" className="text-sm hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-white transition-colors">Request a Demo</Link></li>
              <li><Link to="/login" className="text-sm hover:text-white transition-colors">Log In</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-sm hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Help & Docs */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Help & Docs</h4>
            <ul className="space-y-3">
              <li><Link to="/docs" className="text-sm hover:text-white transition-colors">Documentation</Link></li>
              <li><Link to="/docs/getting-started/setup" className="text-sm hover:text-white transition-colors">Getting Started</Link></li>
              <li><Link to="/docs/security/popia" className="text-sm hover:text-white transition-colors">POPIA Compliance</Link></li>
              <li><Link to="/docs/security/data-export" className="text-sm hover:text-white transition-colors">Data Export</Link></li>
              <li><Link to="/legal/terms" className="text-sm hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/legal/subprocessors" className="text-sm hover:text-white transition-colors">Subprocessors</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Stay Updated</h4>
            {submitted ? (
              <p className="text-sm text-secondary-400">Thanks for subscribing!</p>
            ) : (
              <form onSubmit={handleNewsletter} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <label className="flex items-start space-x-2 text-xs text-gray-400 dark:text-gray-500">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    className="mt-0.5 rounded border-gray-600 text-primary-500 focus:ring-primary-500"
                    required
                  />
                  <span>I agree to receive marketing emails. You can unsubscribe at any time.</span>
                </label>
                <button
                  type="submit"
                  disabled={submitting || !email || !consent}
                  className="w-full px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} Sprout Education. All rights reserved.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            POPIA compliant. Your data is secure and never shared without consent.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default MarketingFooter;
